/**
 * Turning API values into something a person reads.
 *
 * No React and no network. Anything here is used by at least two screens; a
 * one-off phrasing belongs next to the screen that says it.
 *
 * These are **not** pure: the ones that produce words read the active language
 * from the i18next singleton, and the ones that format a date or a number pass
 * it to `Intl`. That is deliberate — threading a locale through every call site
 * would put it in a hundred places to serve one decision.
 *
 * The consequence is the thing to remember: **a component that calls these but
 * no `t()` will not re-render when the language changes.** Subscribe it with
 * `useTranslation()`, as `Timestamp` does.
 */
import type { ReleaseInfo } from '@forsys/shared';
import i18n, { currentLanguage } from '../i18n';

/**
 * A byte count, in the unit that makes it readable.
 *
 * Decimal (kB = 1000), not binary: that is what the operating system's file
 * listing says, and disagreeing with it over the size of the file the user just
 * uploaded is a support ticket for nothing.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return i18n.t('common:state.empty');
  if (bytes < 1000) return `${bytes} B`;

  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = bytes / 1000;
  let unit = 0;

  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }

  // One decimal below 10 (9.4 MB), none above (420 MB) — the extra digit stops
  // mattering as the number grows and only adds noise to a column of them.
  // Through Intl, so Czech gets its decimal comma: 9,4 MB.
  const rounded = value < 10 ? Number(value.toFixed(1)) : Math.round(value);
  const digits = value < 10 ? 1 : 0;
  const formatted = new Intl.NumberFormat(currentLanguage(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rounded);
  return `${formatted} ${units[unit]}`;
}

/** A timestamp as a full local date and time. For a tooltip, or a detail row. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return i18n.t('common:state.empty');
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(currentLanguage(), { dateStyle: 'medium', timeStyle: 'short' });
}

/** Just the date. For a column where the time of day is noise. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return i18n.t('common:state.empty');
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(currentLanguage(), { dateStyle: 'medium' });
}

const RELATIVE_UNITS: Array<[limitSeconds: number, seconds: number, unit: Intl.RelativeTimeFormatUnit]> =
  [
    [60, 1, 'second'],
    [3600, 60, 'minute'],
    [86400, 3600, 'hour'],
    [604800, 86400, 'day'],
    [2629800, 604800, 'week'],
    [31557600, 2629800, 'month'],
    [Infinity, 31557600, 'year'],
  ];

/**
 * "3 minutes ago", "in 2 days".
 *
 * Used for anything the reader is scanning for recency — the audit log, a last
 * sign-in. Pair it with the absolute time in a tooltip: relative is faster to
 * read, absolute is the one you need when something is actually wrong.
 */
export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return i18n.t('common:state.never');
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const deltaSeconds = (date.getTime() - Date.now()) / 1000;
  const magnitude = Math.abs(deltaSeconds);
  if (magnitude < 10) return i18n.t('common:state.justNow');

  // Intl does the grammar, including Czech's three plural forms for "day".
  const formatter = new Intl.RelativeTimeFormat(currentLanguage(), { numeric: 'auto' });
  for (const [limit, divisor, unit] of RELATIVE_UNITS) {
    if (magnitude < limit) {
      return formatter.format(Math.round(deltaSeconds / divisor), unit);
    }
  }
  return formatDate(iso);
}

/** A short, stable label for a uuid, for a column too narrow for all 36 characters. */
export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Splits `user.set_password` into its domain and the rest.
 *
 * Permission names are `domain.verb` by construction — the backend groups them
 * on the dot for exactly this reason — so a role editor can render sections
 * without hardcoding the set.
 */
export function splitPermission(permission: string): { domain: string; verb: string } {
  const dot = permission.indexOf('.');
  if (dot === -1) return { domain: permission, verb: '' };
  return { domain: permission.slice(0, dot), verb: permission.slice(dot + 1) };
}

/**
 * `set_password` → `Set password`. For a checkbox label beside the raw name.
 *
 * Not translated, and cannot be: permission names are identifiers the backend
 * defines, so this tidies the identifier rather than naming the concept. The
 * raw name is shown beside it for exactly that reason.
 */
export function humanise(value: string): string {
  const spaced = value.replace(/[._]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * `v42 – Aero (2026-10-21-123)`, the words the CLI and the chat message use.
 *
 * The number, name and build id are identifiers and stay untranslated; only a
 * build the release pipeline did not make gets a translated label.
 */
export function formatRelease(release: ReleaseInfo): string {
  if (release.number === null) return i18n.t('common:release.dev');
  const name = release.name === null ? '' : ` – ${release.name}`;
  return `v${release.number}${name} (${release.build ?? '?'})`;
}
